/**
 * Integration Utilities
 *
 * Provides formatting functions for integration messages and scheduled notifications.
 * Handles styling of warning text and displays integration status indicators.
 */

import { IntegrationResult } from '@/types/upload.types';

/**
 * @interface ScheduledNotifications
 * @property {Object} results - Integration results mapping
 * @property {IntegrationResult} results.sms - SMS integration status
 * @property {IntegrationResult} results.email - Email integration status
 * @property {IntegrationResult} results.backOffice1C - 1C Back Office integration status
 * @property {IntegrationResult} results.okkoCore - OKKO Core integration status
 * @property {IntegrationResult} results.serviceDesk - Service Desk integration status
 */
interface ScheduledNotifications {
  results: {
    sms: IntegrationResult;
    email: IntegrationResult;
    backOffice1C: IntegrationResult;
    okkoCore: IntegrationResult;
    serviceDesk: IntegrationResult;
  };
}

/**
 * Formats integration message with warning styling and integration status indicators
 *
 * Applies orange styling to warning text (УВАГА:) and appends integration status
 * with success/failure indicators (✅/❌) for enabled integrations.
 *
 * @param message - Base message text to format
 * @param scheduledNotifications - Optional scheduled notifications with integration results
 * @returns Formatted message with HTML styling and integration statuses
 * @example
 * const message = "УВАГА: Important notification";
 * const result = formatIntegrationMessage(message, notifications);
 * // Returns: "<span style=\"color: #ed6c02;\">УВАГА: Important notification</span>\n\nІнтеграції: SMS: ✅, Email: ❌"
 */
export const formatIntegrationMessage = (message: string, scheduledNotifications?: ScheduledNotifications): string => {
  let formattedMessage = message;

  // Wrap warning text in yellow span
  if (message.includes('УВАГА:')) {
    formattedMessage = message.replace(/(УВАГА:[^\n]*)/g, '<span style="color: #ed6c02;">$1</span>');
  }

  if (!scheduledNotifications?.results) {
    return formattedMessage;
  }

  const integrationNames = {
    sms: 'SMS',
    email: 'Email',
    backOffice1C: '1C Back Office',
    okkoCore: 'OKKO Core',
    serviceDesk: 'Service Desk'
  };

  const integrationStatuses: string[] = [];

  Object.entries(scheduledNotifications.results).forEach(([key, result]) => {
    if (result.enabled) {
      const name = integrationNames[key as keyof typeof integrationNames];
      const status = result.success ? '✅' : '❌';
      integrationStatuses.push(`${name}: ${status}`);
    }
  });

  if (integrationStatuses.length > 0) {
    return `${formattedMessage}<br><br>Інтеграції: ${integrationStatuses.join(', ')}`;
  }

  return formattedMessage;
};
